import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import {
  Waves,
  MagnifyingGlass,
  CaretDown,
  Question,
  Ticket,
  PaperPlaneTilt,
  Spinner,
  CheckCircle,
  ArrowLeft,
  Envelope,
  Sun,
  Moon,
  List,
  X
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

const FAQ_DATA = [
  {
    title: "Getting Started",
    items: [
      {
        question: "What is URL indexing and why is it important?",
        answer: "URL indexing is the process of getting search engines like Google to discover and add your web pages to their search results. Without indexing, your pages won't appear in search results, which means no organic traffic. Our service accelerates this process from weeks to minutes."
      },
      {
        question: "How fast can index24hrs.com index my URLs?",
        answer: "Our advanced indexing system typically processes URLs within 30 seconds to 2 minutes. The exact time depends on the URL and Google's current crawl capacity, but most URLs are indexed within 60 seconds."
      },
      {
        question: "How do I get started?",
        answer: "Simply create an account, then purchase a credit plan to start indexing. Submit your URLs through the dashboard, and watch them get indexed in real-time."
      }
    ]
  },
  {
    title: "Submissions & Credits",
    items: [
      {
        question: "How many URLs can I submit at once?",
        answer: "You can submit up to 150 URLs in a single batch submission. There's no limit on the number of batches you can submit, as long as you have sufficient credits."
      },
      {
        question: "What types of URLs work best?",
        answer: "Our service works with any publicly accessible URL. This includes blog posts, product pages, landing pages, news articles, and more. The URL must be accessible to Google's crawler for successful indexing."
      },
      {
        question: "How are credits deducted?",
        answer: "One credit is used per URL submitted for indexing. Credits are deducted at the time of submission. If a URL fails to be processed, the credit is not refunded automatically — contact support for assistance."
      }
    ]
  },
  {
    title: "API & Integration",
    items: [
      {
        question: "Do you offer an API for automated indexing?",
        answer: "Yes! All paid plans include API access. You can automate URL submission through our REST API, making it easy to integrate with your existing workflows and CMS systems."
      },
      {
        question: "Where can I find API documentation?",
        answer: "API documentation is available in your dashboard after purchasing a plan. It includes endpoint details, authentication instructions, code examples in multiple languages, and rate limit information."
      }
    ]
  },
  {
    title: "Payments & Billing",
    items: [
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major cryptocurrencies including Bitcoin (BTC), Ethereum (ETH), USDT (TRC20 & BSC), Litecoin (LTC), Dogecoin (DOGE), Tron (TRX), Binance Coin (BNB), Ripple (XRP), Solana (SOL), and Polygon (MATIC)."
      },
      {
        question: "How long does payment confirmation take?",
        answer: "Payment confirmation depends on the cryptocurrency used. Bitcoin typically takes 10-30 minutes for confirmation. USDT and other tokens on fast networks can confirm within 1-5 minutes. Credits are added automatically once payment is confirmed."
      },
      {
        question: "How do I get started?",
        answer: "Create an account and purchase a credit plan from our pricing page. Plans start at $40 for 1,000 URLs. Credits are added instantly after payment confirmation."
      }
    ]
  }
];

const TICKET_CATEGORIES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'credits', label: 'Credits & Submissions' },
  { value: 'api', label: 'API Support' }
];

function AccordionItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="border rounded-lg overflow-hidden transition-all duration-200"
      style={{ borderColor: isOpen ? 'var(--border-accent)' : 'var(--border-main)', background: 'var(--bg-card)', boxShadow: 'var(--shadow-card)' }}
      data-testid={`faq-item-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium text-sm pr-4">{question}</span>
        <CaretDown
          weight="bold"
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-muted)' }}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 text-sm leading-relaxed border-t pt-3" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-main)' }}>
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('faq');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketCategory, setTicketCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState('');

  const filteredFAQ = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_DATA;
    const q = searchQuery.toLowerCase();
    return FAQ_DATA.map(category => ({
      ...category,
      items: category.items.filter(
        item =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q)
      )
    })).filter(category => category.items.length > 0);
  }, [searchQuery]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/support/tickets`,
        { subject: ticketSubject, message: ticketMessage, category: ticketCategory },
        { withCredentials: true }
      );
      setSubmittedTicketId(response.data.ticket_id);
      setTicketSubmitted(true);
      toast.success('Ticket submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit ticket');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="support-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>

          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/about" className="text-sm" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" className="text-sm" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-pricing">Pricing</Link>
            <Link to="/docs" className="text-sm" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            <Link to="/support" className="text-sm font-medium" style={{ color: 'var(--accent)' }} data-testid="nav-support">Support</Link>
            {user ? (
              <Link to="/dashboard" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-dashboard">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="nav-login">Login</Link>
                <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">Get Started</Link>
              </>
            )}
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="support-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }} data-testid="support-mobile-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" data-testid="support-mobile-menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t px-4 py-4 space-y-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Pricing</Link>
            <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            {user ? (
              <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Dashboard</Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Login</Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn-primary text-sm text-center block">Get Started</Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="support-back-home">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
            <Question weight="fill" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span className="text-xs tracking-wider uppercase font-semibold" style={{ color: 'var(--accent)' }}>Support Center</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4" data-testid="support-title">
            How Can We <span style={{ color: 'var(--accent)' }}>Help?</span>
          </h1>
          <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Find answers in our FAQ or create a support ticket
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-4xl mx-auto flex justify-center gap-4">
          <button
            onClick={() => setActiveTab('faq')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all"
            style={activeTab === 'faq'
              ? { background: 'var(--accent)', color: '#ffffff' }
              : { background: 'var(--bg-card)', border: '1px solid var(--border-main)', color: 'var(--text-secondary)' }
            }
            data-testid="tab-faq"
          >
            <Question weight="bold" className="w-4 h-4" />
            FAQ
          </button>
          <button
            onClick={() => setActiveTab('ticket')}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all"
            style={activeTab === 'ticket'
              ? { background: 'var(--accent)', color: '#ffffff' }
              : { background: 'var(--bg-card)', border: '1px solid var(--border-main)', color: 'var(--text-secondary)' }
            }
            data-testid="tab-ticket"
          >
            <Ticket weight="bold" className="w-4 h-4" />
            Create Ticket
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      {activeTab === 'faq' && (
        <section className="pb-24 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Search */}
            <div className="relative mb-10">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search FAQ..."
                className="input-field pl-12"
                data-testid="faq-search"
              />
            </div>

            {/* FAQ Categories */}
            <div className="space-y-10">
              {filteredFAQ.map((category, catIndex) => (
                <div key={catIndex}>
                  <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>{category.title}</h2>
                  <div className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <AccordionItem key={itemIndex} question={item.question} answer={item.answer} />
                    ))}
                  </div>
                </div>
              ))}
              {filteredFAQ.length === 0 && (
                <div className="text-center py-12">
                  <p style={{ color: 'var(--text-secondary)' }}>No results found for "{searchQuery}"</p>
                </div>
              )}
            </div>

            {/* CTA to create ticket */}
            <div className="mt-16 border rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
              <h3 className="text-xl font-bold mb-2">Still need help?</h3>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                Can't find what you're looking for? Create a support ticket or email us directly.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                {user ? (
                  <button
                    onClick={() => setActiveTab('ticket')}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="create-ticket-cta"
                  >
                    <Ticket weight="bold" className="w-5 h-5" />
                    Create Ticket
                  </button>
                ) : (
                  <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="login-to-ticket">
                    Login to Create Ticket
                  </Link>
                )}
                <Link to="/support" className="inline-flex items-center gap-2 text-sm font-medium hover:underline" style={{ color: 'var(--accent)' }} data-testid="support-whatsapp-link">
                  <Envelope weight="fill" className="w-4 h-4" /> Submit a Ticket
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Ticket Section */}
      {activeTab === 'ticket' && (
        <section className="pb-24 px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            {!user ? (
              <div className="border rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <Ticket weight="duotone" className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--accent)' }} />
                <h3 className="text-xl font-bold mb-2">Login Required</h3>
                <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>You need to be logged in to create a support ticket.</p>
                <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="login-for-ticket">
                  Login to Continue
                </Link>
              </div>
            ) : ticketSubmitted ? (
              <div className="border rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'rgba(16,185,129,0.2)', boxShadow: 'var(--shadow-card)' }}>
                <CheckCircle weight="fill" className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Ticket Submitted!</h3>
                <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Your ticket ID is:</p>
                <p className="font-mono font-bold text-lg mb-6" style={{ color: 'var(--accent)' }} data-testid="ticket-id">{submittedTicketId}</p>
                <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>We'll get back to you as soon as possible.</p>
                <button
                  onClick={() => {
                    setTicketSubmitted(false);
                    setTicketSubject('');
                    setTicketMessage('');
                    setTicketCategory('general');
                  }}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Submit Another Ticket
                </button>
              </div>
            ) : (
              <div className="border rounded-xl p-8" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
                <h3 className="text-xl font-bold mb-6">Create Support Ticket</h3>
                <form onSubmit={handleSubmitTicket} className="space-y-5">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Category</label>
                    <select
                      value={ticketCategory}
                      onChange={(e) => setTicketCategory(e.target.value)}
                      className="input-field"
                      data-testid="ticket-category"
                    >
                      {TICKET_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Subject</label>
                    <input
                      type="text"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      placeholder="Brief description of your issue"
                      className="input-field"
                      data-testid="ticket-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Message</label>
                    <textarea
                      value={ticketMessage}
                      onChange={(e) => setTicketMessage(e.target.value)}
                      placeholder="Please describe your issue in detail..."
                      rows={6}
                      className="input-field resize-none"
                      data-testid="ticket-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="submit-ticket-btn"
                  >
                    {submitting ? (
                      <Spinner className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <PaperPlaneTilt weight="bold" className="w-5 h-5" />
                        Submit Ticket
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-bold text-sm">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </div>
          <p className="text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>
            © 2026 index24hrs.com. Instant indexing for the modern web.
          </p>
        </div>
      </footer>
    </div>
  );
}
